import React, { useEffect, useMemo, useState } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Capacitor } from '@capacitor/core';
import { formatDateToLocalString, formatDurusmaTarihi, formatDateForDisplay } from '../../utils/dateUtils';
import { downloadBlob } from '../../utils/fileDownload';
import './Takvim.css';
import KayanSecici from '../Common/KayanSecici';
import { YENI_TASARIM } from '../../utils/yeniTasarim';

/*
  TAKVIM
  ======
  NEDEN YENIDEN YAZILDI (2026-09-30): kullanici eski takvimi "begenmedigim
  ve kullanisli olmadigi icin kullanmiyorum" dedi. Eski halde:
  - ay gorunumunde her gunde yalnizca renkli NOKTALAR vardi; ne oldugunu
    gormek icin tek tek gune tiklamak gerekiyordu,
  - dort gorunum (Ay / Ajanda / Hafta / Durusmalar) birbirinin tekrariydi,
  - tahkim basvurusu YAPILMIS dosyalar bile takvimde kaliyordu.

  SIMDI:
  - Ay gorunumunde her gunun icinde olaylar yaziyla (saat + kisa ad).
  - Uc gorunum: Ay, Hafta, Liste. Telefonda varsayilan Liste.
  - Ustte tur filtreleri (Durusma, Tahkim, Zamanasimi, Rapor, Is, Not),
    her birinin bu donemdeki sayisiyla.
  - "Onumuzdeki 7 gun" ozeti: en cok ise yarayan bilgi en ustte.
  - Tahkim: basvurusu yapildi isaretlenenler dusuyor (panodaki kuralla ayni).

  VERI: bu bilesen veriyi kendisi CEKMEZ; sayfa (pages/Calendar.js) ceker
  ve buraya verir. Boylece ayni gorunum ornek veriyle onizlenebiliyor.
*/

/* ---------- Turler ---------- */

export const TURLER = {
  durusma: { ad: 'Duruşma', renk: '#2b3652', acik: 'rgba(43, 54, 82, 0.10)' },
  tahkim: { ad: 'Tahkim', renk: '#7c56b8', acik: 'rgba(124, 86, 184, 0.12)' },
  zamanasimi: { ad: 'Zamanaşımı', renk: '#c0392b', acik: 'rgba(192, 57, 43, 0.10)' },
  rapor: { ad: 'Rapor', renk: '#b8862b', acik: 'rgba(201, 162, 74, 0.16)' },
  is: { ad: 'İş', renk: '#1f7a6d', acik: 'rgba(31, 122, 109, 0.11)' },
  not: { ad: 'Not', renk: '#5b6472', acik: 'rgba(91, 100, 114, 0.11)' },
};
const TUR_SIRASI = ['durusma', 'tahkim', 'zamanasimi', 'rapor', 'is', 'not'];

const AYLAR = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const GUNLER_KISA = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
const GUNLER_UZUN = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];

/* ---------- Gun anahtarlari ----------
   Takvim hesaplari "YYYY-MM-DD" anahtarlariyla yapiliyor. Saat dilimi
   karmasasi olmasin diye gunler yil/ay/gun sayilarindan uretiliyor;
   olay tarihleri ise sistemin Istanbul saatine gore anahtarlaniyor. */

const ikiHane = (n) => String(n).padStart(2, '0');
const anahtar = (y, m, d) => `${y}-${ikiHane(m)}-${ikiHane(d)}`;
const tariheCevir = (k) => { const [y, m, d] = k.split('-').map(Number); return new Date(y, m - 1, d, 12); };
const anahtaraCevir = (t) => anahtar(t.getFullYear(), t.getMonth() + 1, t.getDate());
const gunEkle = (k, n) => { const t = tariheCevir(k); t.setDate(t.getDate() + n); return anahtaraCevir(t); };
const haftaBasi = (k) => { const t = tariheCevir(k); const g = (t.getDay() + 6) % 7; t.setDate(t.getDate() - g); return anahtaraCevir(t); };
const bugunAnahtari = () => anahtaraCevir(new Date());

const saatMetni = (deger) => {
  const m = formatDurusmaTarihi(deger);
  return m && m.includes(' ') ? m.split(' ')[1] : '';
};

const al = (o, ...adlar) => { for (const a of adlar) { if (o?.[a] !== undefined && o?.[a] !== null) return o[a]; } return undefined; };

/* ---------- Veriyi olaylara cevir ---------- */

export const olaylariHazirla = (dosyalar = [], isler = [], notlar = []) => {
  const liste = [];

  dosyalar.forEach((f) => {
    const id = al(f, 'Id', 'id');
    const foyNo = al(f, 'FoyNo', 'foyNo');
    const muvekkil = al(f, 'Muvekkil', 'muvekkil') || 'Müvekkil belirtilmemiş';
    const mahkeme = al(f, 'MahkemaBilgisi', 'mahkemaBilgisi') || '';
    const ortak = { dosyaId: id, foyNo, muvekkil, mahkeme };

    const ekle = (tur, deger, ek = {}) => {
      if (!deger) return;
      const gun = formatDateToLocalString(deger);
      if (!gun) return;
      liste.push({ anahtar: `${tur}-${id}-${gun}`, tur, gun, ham: deger, baslik: muvekkil, ...ortak, ...ek });
    };

    const durusma = al(f, 'DurusmaTarihi', 'durusmaTarihi');
    ekle('durusma', durusma, { saat: saatMetni(durusma) });

    // Basvurusu yapilmis tahkim artik takvimde yer tutmuyor.
    if (!al(f, 'TahkimBasvuruYapildi', 'tahkimBasvuruYapildi')) {
      ekle('tahkim', al(f, 'TahkimBasvuruTarihi', 'tahkimBasvuruTarihi'));
    }
    ekle('zamanasimi', al(f, 'ZamanAsimiTarihi', 'zamanAsimiTarihi'));
    ekle('rapor', al(f, 'RaporTarihi', 'raporTarihi'));
  });

  isler.forEach((j) => {
    const deger = al(j, 'DueDate', 'dueDate');
    if (!deger) return;
    const gun = formatDateToLocalString(deger);
    if (!gun) return;
    const tamam = !!al(j, 'IsCompleted', 'isCompleted') || al(j, 'Status', 'status') === 2;
    liste.push({
      anahtar: `is-${al(j, 'Id', 'id')}`,
      tur: 'is', gun, ham: deger,
      baslik: al(j, 'Description', 'description') || 'İş',
      foyNo: al(j, 'FoyNo', 'foyNo'),
      muvekkil: al(j, 'Muvekkil', 'muvekkil') || '',
      dosyaId: al(j, 'CaseFileId', 'caseFileId'),
      tamam,
    });
  });

  notlar.forEach((n) => {
    const deger = al(n, 'NoteDate', 'noteDate');
    if (!deger) return;
    const gun = formatDateToLocalString(deger);
    if (!gun) return;
    liste.push({
      anahtar: `not-${al(n, 'Id', 'id')}`,
      tur: 'not', gun, ham: deger,
      baslik: al(n, 'Title', 'title') || 'Not',
      icerik: al(n, 'Content', 'content') || '',
    });
  });

  // Gun icinde: once saatli olanlar saat sirasiyla, sonra tur sirasi.
  liste.sort((a, b) => {
    if (a.gun !== b.gun) return a.gun < b.gun ? -1 : 1;
    const sa = a.saat || '99:99';
    const sb = b.saat || '99:99';
    if (sa !== sb) return sa < sb ? -1 : 1;
    return TUR_SIRASI.indexOf(a.tur) - TUR_SIRASI.indexOf(b.tur);
  });
  return liste;
};

/* ---------- Kucuk parcalar ---------- */

const TurNoktasi = ({ tur }) => (
  <span className="tk-nokta" style={{ background: TURLER[tur].renk }} aria-hidden="true" />
);

/** Ay hucresindeki tek satirlik olay */
const OlayHap = ({ olay, onSec }) => {
  const t = TURLER[olay.tur];
  return (
    <button
      type="button"
      className={`tk-hap${olay.tamam ? ' tamam' : ''}`}
      style={{ '--tur': t.renk, '--tur-acik': t.acik }}
      onClick={(e) => { e.stopPropagation(); onSec(olay); }}
      title={`${t.ad}: ${olay.baslik}`}
    >
      {olay.saat && <span className="tk-hap-saat">{olay.saat}</span>}
      <span className="tk-hap-ad">{olay.baslik}</span>
    </button>
  );
};

/** Liste / panel / hafta icindeki olay karti */
const OlayKarti = ({ olay, onAc, gecmis }) => {
  const t = TURLER[olay.tur];
  const tiklanir = !!olay.dosyaId || olay.tur === 'is';
  const alt = [
    olay.foyNo ? `Föy ${olay.foyNo}` : null,
    olay.tur === 'is' && olay.muvekkil ? olay.muvekkil : null,
    olay.tur !== 'is' && olay.tur !== 'not' && olay.mahkeme ? olay.mahkeme : null,
  ].filter(Boolean).join(' · ');

  const Etiket = tiklanir ? 'button' : 'div';
  return (
    <Etiket
      type={tiklanir ? 'button' : undefined}
      className={`tk-kart${tiklanir ? ' tiklanir' : ''}${olay.tamam ? ' tamam' : ''}${gecmis ? ' gecmis' : ''}`}
      style={{ '--tur': t.renk, '--tur-acik': t.acik }}
      onClick={tiklanir ? () => onAc(olay) : undefined}
    >
      <span className="tk-kart-sol">
        <span className="tk-kart-tur">{t.ad}</span>
        {olay.saat && <span className="tk-kart-saat">{olay.saat}</span>}
      </span>
      <span className="tk-kart-govde">
        <span className="tk-kart-baslik">{olay.baslik}</span>
        {alt && <span className="tk-kart-alt">{alt}</span>}
        {olay.tur === 'not' && olay.icerik && <span className="tk-kart-alt">{olay.icerik}</span>}
        {olay.tamam && <span className="tk-kart-rozet">Tamamlandı</span>}
      </span>
    </Etiket>
  );
};

const gunBasligi = (k, bugun) => {
  const t = tariheCevir(k);
  const ekler = [];
  if (k === bugun) ekler.push('Bugün');
  else if (k === gunEkle(bugun, 1)) ekler.push('Yarın');
  else if (k === gunEkle(bugun, -1)) ekler.push('Dün');
  return {
    ana: `${t.getDate()} ${AYLAR[t.getMonth()]} ${GUNLER_UZUN[t.getDay()]}`,
    ek: ekler[0] || '',
  };
};

/* ---------- Ana bilesen ---------- */

const GORUNUM_ANAHTARI = 'takvim.gorunum';
const FILTRE_ANAHTARI = 'takvim.filtre';

const TakvimGorunumu = ({ dosyalar, isler, notlar, yukleniyor = false, onDosyaAc, onIsAc, baslangicGunu }) => {
  const bugun = bugunAnahtari();

  const [odak, setOdak] = useState(baslangicGunu || bugun);
  const [secili, setSecili] = useState(baslangicGunu || bugun);
  const [gorunum, setGorunum] = useState(() => {
    try {
      const k = localStorage.getItem(GORUNUM_ANAHTARI);
      if (k === 'ay' || k === 'hafta' || k === 'liste') return k;
    } catch (e) { /* yoksay */ }
    // Telefonda ay izgarasi dar kaliyor; varsayilan liste.
    return (typeof window !== 'undefined' && window.matchMedia?.('(max-width: 720px)').matches) ? 'liste' : 'ay';
  });
  const [kapaliTurler, setKapaliTurler] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(FILTRE_ANAHTARI) || '[]')); } catch (e) { return new Set(); }
  });

  useEffect(() => { try { localStorage.setItem(GORUNUM_ANAHTARI, gorunum); } catch (e) { /* yoksay */ } }, [gorunum]);
  useEffect(() => { try { localStorage.setItem(FILTRE_ANAHTARI, JSON.stringify([...kapaliTurler])); } catch (e) { /* yoksay */ } }, [kapaliTurler]);

  const tumOlaylar = useMemo(() => olaylariHazirla(dosyalar, isler, notlar), [dosyalar, isler, notlar]);
  const olaylar = useMemo(() => tumOlaylar.filter((o) => !kapaliTurler.has(o.tur)), [tumOlaylar, kapaliTurler]);

  const gunlereGore = useMemo(() => {
    const m = new Map();
    olaylar.forEach((o) => { if (!m.has(o.gun)) m.set(o.gun, []); m.get(o.gun).push(o); });
    return m;
  }, [olaylar]);

  const [oy, om] = odak.split('-').map(Number);
  const ayBasi = anahtar(oy, om, 1);
  const aySonu = anahtar(oy, om, new Date(oy, om, 0).getDate());
  const hBasi = haftaBasi(odak);
  const hSonu = gunEkle(hBasi, 6);

  const donemBas = gorunum === 'hafta' ? hBasi : ayBasi;
  const donemSon = gorunum === 'hafta' ? hSonu : aySonu;

  // Filtre cipleri icin sayilar: gecerli donemde, filtreden BAGIMSIZ.
  const turSayilari = useMemo(() => {
    const s = {};
    tumOlaylar.forEach((o) => { if (o.gun >= donemBas && o.gun <= donemSon) s[o.tur] = (s[o.tur] || 0) + 1; });
    return s;
  }, [tumOlaylar, donemBas, donemSon]);

  // Ustteki ozet: onumuzdeki 7 gun.
  const ozet = useMemo(() => {
    const son = gunEkle(bugun, 6);
    const aralik = tumOlaylar.filter((o) => o.gun >= bugun && o.gun <= son && !o.tamam);
    const say = (tur) => aralik.filter((o) => o.tur === tur).length;
    return {
      bugun: tumOlaylar.filter((o) => o.gun === bugun && !o.tamam).length,
      durusma: say('durusma'),
      tahkim: say('tahkim'),
      zamanasimi: tumOlaylar.filter((o) => o.tur === 'zamanasimi' && o.gun >= bugun && o.gun <= gunEkle(bugun, 29)).length,
    };
  }, [tumOlaylar, bugun]);

  /* ---- Gezinme ---- */
  const oncekine = () => {
    if (gorunum === 'hafta') setOdak(gunEkle(odak, -7));
    else setOdak(anahtar(om === 1 ? oy - 1 : oy, om === 1 ? 12 : om - 1, 1));
  };
  const sonrakine = () => {
    if (gorunum === 'hafta') setOdak(gunEkle(odak, 7));
    else setOdak(anahtar(om === 12 ? oy + 1 : oy, om === 12 ? 1 : om + 1, 1));
  };
  const bugune = () => { setOdak(bugun); setSecili(bugun); };

  const turDegistir = (tur) => {
    setKapaliTurler((onceki) => {
      const y = new Set(onceki);
      if (y.has(tur)) y.delete(tur); else y.add(tur);
      return y;
    });
  };

  const olayAc = (o) => {
    if (o.tur === 'is') { onIsAc?.(o); return; }
    if (o.dosyaId) onDosyaAc?.(o.dosyaId);
  };

  const baslik = gorunum === 'hafta'
    ? (() => {
      const a = tariheCevir(hBasi); const b = tariheCevir(hSonu);
      return a.getMonth() === b.getMonth()
        ? `${a.getDate()} – ${b.getDate()} ${AYLAR[b.getMonth()]} ${b.getFullYear()}`
        : `${a.getDate()} ${AYLAR[a.getMonth()]} – ${b.getDate()} ${AYLAR[b.getMonth()]} ${b.getFullYear()}`;
    })()
    : `${AYLAR[om - 1]} ${oy}`;

  /* ---- Durusma listesi: PDF ve yazdir (eski takvimdeki islev korunuyor) ---- */
  const aylikDurusmalar = () => tumOlaylar.filter((o) => o.tur === 'durusma' && o.gun >= ayBasi && o.gun <= aySonu);

  const pdfUret = () => {
    const list = aylikDurusmalar();
    const doc = new jsPDF('p', 'mm', 'a4');
    doc.setFontSize(14);
    doc.text(`Durusma Listesi - ${AYLAR[om - 1]} ${oy}`, 14, 16);
    doc.setFontSize(10);
    doc.text(`Toplam ${list.length} durusma`, 14, 22);
    autoTable(doc, {
      startY: 26,
      head: [['Tarih ve Saat', 'Foy No', 'Muvekkil', 'Mahkeme Bilgisi']],
      body: list.map((o) => [formatDateForDisplay(o.ham) || '-', String(o.foyNo ?? ''), o.muvekkil || '-', o.mahkeme || '-']),
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [43, 54, 82], textColor: 255 },
      columnStyles: { 0: { cellWidth: 36 }, 1: { cellWidth: 18 }, 2: { cellWidth: 52 }, 3: { cellWidth: 'auto' } },
    });
    return { blob: doc.output('blob'), ad: `Durusmalar_${oy}_${ikiHane(om)}.pdf` };
  };

  const durusmaListesi = async () => {
    const list = aylikDurusmalar();
    if (!list.length) { window.alert('Bu ay için duruşma yok.'); return; }
    try {
      const yerli = typeof Capacitor?.isNativePlatform === 'function' && Capacitor.isNativePlatform();
      if (yerli) {
        const { blob, ad } = pdfUret();
        await downloadBlob(blob, ad, 'application/pdf');
        return;
      }
      const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const satirlar = list.map((o) => `<tr><td>${esc(formatDateForDisplay(o.ham))}</td><td>${esc(o.foyNo)}</td><td>${esc(o.muvekkil)}</td><td>${esc(o.mahkeme || '-')}</td></tr>`).join('');
      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Duruşmalar - ${AYLAR[om - 1]} ${oy}</title>
        <style>body{font-family:Arial,sans-serif;padding:20px}h1{text-align:center;color:#2b3652;font-size:18px;margin:0 0 6px}
        p{text-align:center;color:#666;margin:0 0 16px}table{width:100%;border-collapse:collapse}
        th,td{border:1px solid #ddd;padding:9px 8px;text-align:left;font-size:12px}th{background:#2b3652;color:#fff}
        tr:nth-child(even){background:#f7f6f2}@media print{body{padding:0}tr{page-break-inside:avoid}}</style></head><body>
        <h1>Duruşma Listesi - ${AYLAR[om - 1]} ${oy}</h1><p>Toplam ${list.length} duruşma</p>
        <table><thead><tr><th>Tarih ve Saat</th><th>Föy No</th><th>Müvekkil</th><th>Mahkeme Bilgisi</th></tr></thead><tbody>${satirlar}</tbody></table></body></html>`;
      const iframe = document.createElement('iframe');
      iframe.setAttribute('style', 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;');
      document.body.appendChild(iframe);
      const idoc = iframe.contentWindow?.document;
      if (!idoc) { document.body.removeChild(iframe); const { blob, ad } = pdfUret(); await downloadBlob(blob, ad, 'application/pdf'); return; }
      idoc.open(); idoc.write(html); idoc.close();
      setTimeout(() => {
        try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (e) { /* yoksay */ }
        setTimeout(() => { try { document.body.removeChild(iframe); } catch (e) { /* yoksay */ } }, 1000);
      }, 300);
    } catch (err) {
      window.alert('Liste hazırlanamadı: ' + (err?.message || 'Bilinmeyen hata'));
    }
  };

  /* ---- Gorunumler ---- */

  const ayIzgarasi = () => {
    const ilkGun = (new Date(oy, om - 1, 1).getDay() + 6) % 7;
    const baslangic = gunEkle(ayBasi, -ilkGun);
    const hucreler = Array.from({ length: 42 }, (_, i) => gunEkle(baslangic, i));
    // Son satir tamamen sonraki aya dusuyorsa gosterme.
    const gosterilen = hucreler[35] > aySonu ? hucreler.slice(0, 35) : hucreler;
    const seciliOlaylar = gunlereGore.get(secili) || [];
    const sb = gunBasligi(secili, bugun);

    return (
      <div className="tk-ay-yerlesim">
        <div className="tk-ay" key={ayBasi}>
          <div className="tk-ay-basliklar">
            {GUNLER_KISA.map((g, i) => <div key={g} className={i >= 5 ? 'hafta-sonu' : ''}>{g}</div>)}
          </div>
          <div className="tk-ay-gunler">
            {gosterilen.map((k) => {
              const liste = gunlereGore.get(k) || [];
              const baskaAy = k < ayBasi || k > aySonu;
              const sinif = ['tk-gun',
                baskaAy && 'baska-ay', k === bugun && 'bugun', k === secili && 'secili',
                k < bugun && 'gecmis', liste.length && 'dolu'].filter(Boolean).join(' ');
              return (
                <div key={k} className={sinif} onClick={() => setSecili(k)} role="button" tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') setSecili(k); }}>
                  <div className="tk-gun-ust">
                    <span className="tk-gun-no">{Number(k.slice(8))}</span>
                    {liste.length > 0 && <span className="tk-gun-sayi">{liste.length}</span>}
                  </div>
                  <div className="tk-gun-haplar">
                    {liste.slice(0, 3).map((o) => <OlayHap key={o.anahtar} olay={o} onSec={() => setSecili(k)} />)}
                    {liste.length > 3 && <span className="tk-daha">+{liste.length - 3} daha</span>}
                  </div>
                  {/* Dar ekranda haplar yerine renk noktalari */}
                  <div className="tk-gun-noktalar">
                    {[...new Set(liste.map((o) => o.tur))].slice(0, 4).map((t) => <TurNoktasi key={t} tur={t} />)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <aside className="tk-panel" key={secili}>
          <div className="tk-panel-bas">
            <div>
              <div className="tk-panel-tarih">{sb.ana}</div>
              {sb.ek && <div className="tk-panel-ek">{sb.ek}</div>}
            </div>
            <span className="tk-panel-sayi">{seciliOlaylar.length}</span>
          </div>
          {seciliOlaylar.length === 0 ? (
            <div className="tk-bos">Bu gün için kayıt yok.</div>
          ) : (
            <div className="tk-kartlar">
              {seciliOlaylar.map((o) => <OlayKarti key={o.anahtar} olay={o} onAc={olayAc} gecmis={secili < bugun} />)}
            </div>
          )}
        </aside>
      </div>
    );
  };

  const haftaGorunumu = () => {
    const gunler = Array.from({ length: 7 }, (_, i) => gunEkle(hBasi, i));
    return (
      <div className="tk-hafta" key={hBasi}>
        {gunler.map((k, i) => {
          const liste = gunlereGore.get(k) || [];
          const t = tariheCevir(k);
          return (
            <section key={k} className={`tk-hafta-gun${k === bugun ? ' bugun' : ''}${k < bugun ? ' gecmis' : ''}${i >= 5 ? ' hafta-sonu' : ''}`}>
              <header>
                <span className="tk-hafta-gunadi">{GUNLER_KISA[i]}</span>
                <span className="tk-hafta-no">{t.getDate()}</span>
                {liste.length > 0 && <span className="tk-gun-sayi">{liste.length}</span>}
              </header>
              <div className="tk-kartlar">
                {liste.length === 0
                  ? <div className="tk-bos kucuk">—</div>
                  : liste.map((o) => <OlayKarti key={o.anahtar} olay={o} onAc={olayAc} gecmis={k < bugun} />)}
              </div>
            </section>
          );
        })}
      </div>
    );
  };

  const listeGorunumu = () => {
    const gunler = [...gunlereGore.keys()].filter((k) => k >= ayBasi && k <= aySonu).sort();
    if (!gunler.length) return <div className="tk-bos buyuk">{AYLAR[om - 1]} ayında kayıt yok.</div>;
    return (
      <div className="tk-liste" key={ayBasi}>
        {gunler.map((k) => {
          const b = gunBasligi(k, bugun);
          return (
            <section key={k} className={`tk-liste-gun${k === bugun ? ' bugun' : ''}${k < bugun ? ' gecmis' : ''}`}>
              <header>
                <span className="tk-liste-tarih">{b.ana}</span>
                {b.ek && <span className="tk-liste-ek">{b.ek}</span>}
              </header>
              <div className="tk-kartlar">
                {gunlereGore.get(k).map((o) => <OlayKarti key={o.anahtar} olay={o} onAc={olayAc} gecmis={k < bugun} />)}
              </div>
            </section>
          );
        })}
      </div>
    );
  };

  return (
    <div className="tk">
      {/* Ozet: onumuzdeki gunler */}
      <div className="tk-ozet">
        <div className="tk-ozet-kart"><span className="tk-ozet-sayi">{ozet.bugun}</span><span className="tk-ozet-ad">Bugün</span></div>
        <div className="tk-ozet-kart"><span className="tk-ozet-sayi">{ozet.durusma}</span><span className="tk-ozet-ad">Duruşma · 7 gün</span></div>
        <div className="tk-ozet-kart"><span className="tk-ozet-sayi">{ozet.tahkim}</span><span className="tk-ozet-ad">Tahkim · 7 gün</span></div>
        <div className={`tk-ozet-kart${ozet.zamanasimi ? ' uyari' : ''}`}><span className="tk-ozet-sayi">{ozet.zamanasimi}</span><span className="tk-ozet-ad">Zamanaşımı · 30 gün</span></div>
      </div>

      {/* Ust cubuk */}
      <div className="tk-cubuk">
        <div className="tk-gezinme">
          <button type="button" className="tk-ok" onClick={oncekine} aria-label="Önceki">‹</button>
          <button type="button" className="tk-bugun" onClick={bugune}>Bugün</button>
          <button type="button" className="tk-ok" onClick={sonrakine} aria-label="Sonraki">›</button>
          <h2 className="tk-baslik">{baslik}</h2>
        </div>
        <div className="tk-sag">
          {YENI_TASARIM ? (
            <KayanSecici
              className="tk-ks"
              etiket="Takvim görünümü"
              secili={gorunum}
              onSec={setGorunum}
              secenekler={[{ anahtar: 'ay', etiket: 'Ay' }, { anahtar: 'hafta', etiket: 'Hafta' }, { anahtar: 'liste', etiket: 'Liste' }]}
            />
          ) : (
          <div className="tk-gorunumler" role="tablist">
            {[['ay', 'Ay'], ['hafta', 'Hafta'], ['liste', 'Liste']].map(([k, ad]) => (
              <button key={k} type="button" role="tab" aria-selected={gorunum === k}
                className={gorunum === k ? 'aktif' : ''} onClick={() => setGorunum(k)}>{ad}</button>
            ))}
          </div>
          )}
          <button type="button" className="tk-yazdir" onClick={durusmaListesi} title="Bu ayın duruşma listesi">
            Duruşma listesi
          </button>
        </div>
      </div>

      {/* Tur filtreleri */}
      <div className="tk-filtreler">
        {TUR_SIRASI.map((tur) => {
          const t = TURLER[tur];
          const kapali = kapaliTurler.has(tur);
          return (
            <button key={tur} type="button" className={`tk-filtre${kapali ? ' kapali' : ''}`}
              style={{ '--tur': t.renk, '--tur-acik': t.acik }} onClick={() => turDegistir(tur)}
              aria-pressed={!kapali}>
              <TurNoktasi tur={tur} />
              {t.ad}
              <span className="tk-filtre-sayi">{turSayilari[tur] || 0}</span>
            </button>
          );
        })}
      </div>

      {yukleniyor ? (
        <div className="tk-bos buyuk">Takvim yükleniyor…</div>
      ) : (
        <div className="tk-icerik">
          {gorunum === 'ay' && ayIzgarasi()}
          {gorunum === 'hafta' && haftaGorunumu()}
          {gorunum === 'liste' && listeGorunumu()}
        </div>
      )}
    </div>
  );
};

export default TakvimGorunumu;
